import { TextField as TextFieldBase } from '@mui/material';
import { ComponentPropsWithoutRef, ElementRef, forwardRef } from 'react';
import { Field, FieldMetaState, FieldRenderProps } from 'react-final-form';

export type ShowErrorFunc<TValue = unknown> = (props: ShowErrorProps<TValue>) => boolean;

type ShowErrorProps<TValue> = {
  meta: FieldMetaState<TValue>;
  value?: TValue;
};

const hasValidationError = (meta: FieldMetaState<unknown>) => {
  const { submitError, dirtySinceLastSubmit, error } = meta;

  return (submitError && !dirtySinceLastSubmit) || error;
};

export const showErrorOnChange: ShowErrorFunc = ({ meta }) =>
  !!(hasValidationError(meta) && (meta.touched || meta.modified));

export const showErrorOnBlurOrDirty: ShowErrorFunc = ({ meta }) =>
  !!(hasValidationError(meta) && (meta.touched || meta.dirty));

type TextFieldControlElement = typeof TextFieldBase;
type TextFieldControlProps = Pick<FieldRenderProps<unknown>, 'input' | 'meta'> &
  ComponentPropsWithoutRef<TextFieldControlElement> & {
    showError?: ShowErrorFunc;
  };

const TextFieldControl = forwardRef<ElementRef<TextFieldControlElement>, TextFieldControlProps>((props, ref) => {
  const { input, meta, required, fullWidth = true, helperText, showError = showErrorOnChange, ...rest } = props;

  const { name, value, type, onChange, ...restInput } = input;
  const { error, submitError } = meta;
  const isError = showError({ meta, value });

  return (
    <TextFieldBase
      fullWidth={fullWidth}
      helperText={isError ? error || submitError : helperText}
      error={isError}
      onChange={onChange}
      name={name}
      value={value}
      type={type}
      required={required}
      inputProps={{ required, ...restInput }}
      inputRef={ref}
      onFocus={rest.onFocus || restInput.onFocus}
      onBlur={rest.onBlur || restInput.onBlur}
      {...rest}
    />
  );
});

TextFieldControl.displayName = 'TextFieldControl';

export type TextFieldProps = Partial<Omit<ComponentPropsWithoutRef<typeof TextFieldControl>, 'type' | 'onChange'>> & {
  name: string;
  type?: ComponentPropsWithoutRef<TextFieldControlElement>['type'];
  fieldProps?: Partial<ComponentPropsWithoutRef<typeof Field>>;
  showError?: ShowErrorFunc;
};

export const TextField = forwardRef<ElementRef<typeof TextFieldControl>, TextFieldProps>((props, ref) => {
  const { name, type, fieldProps, ...rest } = props;

  return (
    <Field
      name={name}
      type={type}
      render={({ input, meta }) => (
        <TextFieldControl input={input} meta={meta} name={name} type={type} ref={ref} {...rest} />
      )}
      {...fieldProps}
    />
  );
});

TextField.displayName = 'TextField';
